<template>
  <b-modal
    ref="preformattedModal"
    size="xxl"
    :title="$t('diagnosis.btns.preformatted')"
    centered
    modal-class="preformatted-modal"
    static
    qa="preformatted-modal"
  >
    <!--Table view: item selection-->

    <b-row class="px-2">
      <b-col class="filters-header bottom-bordered pb-2">
        <b-form-input
          v-model="filter"
          qa="preformatted-modal-filter-input"
          type="search"
          :placeholder="$t('diagnosis.labels.filterByTitleAndDescription')"
          class="filter-input form-control-sm"
        />
      </b-col>
      <b-col
        v-if="visibleOnlyCompatibleCheck"
        qa="preformatted-modal-checkbox"
        cols="auto"
        class="filters-header bottom-bordered mt-1"
      >
        <b-form-checkbox
          v-model="customConfig.showOnlyCompatible"
          name="checkbox-text"
          @input="onShowOnlyCompatibleChanged"
        >
          <span class="dark">{{ $t('global.description.modal.compatible') }}</span>
        </b-form-checkbox>
      </b-col>
    </b-row>
    <div
      class="preformatted-table full-bordered"
      qa="preformatted-modal-table"
    >
      <b-container fluid>
        <b-row class="table-header component-inside-1 full-bordered d-flex align-items-center">
          <b-col
            cols="4"
            class="label-dark"
          >
            {{ $t('diagnosis.labels.title') }}
          </b-col>
        </b-row>
        <b-row class="table-content left-bordered right-bordered bottom-bordered">
          <b-col>
            <b-row
              v-for="template in filteredPreformattedList"
              :key="template.id"
              qa="preformatted-modal-table-row"
              class="table-row bottom-bordered py-3 px-2"
              :class="{'row-active': selectedTemplateId === template.id}"
              @click="selectTemplate(template.id)"
            >
              <b-col
                class="table-row-tittle label-dark"
                qa="preformatted-modal-table-row-title"
                cols="4"
              >
                {{ template.title }}
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-container>
      <!--Preformatted preview sidepanel-->
      <div
        v-if="selectedTemplate && preformattedSelectedData"
        class="panel-visible active"
      >
        <div class="template-sidepanel ap-sidepanel d-flex flex-column">
          <div class="header bottom-bordered px-4 py-2 d-flex align-items-center justify-content-between">
            <span
              class="ap-title ap-title__main"
              qa="preformatted-modal-sidepanel-title"
            >{{ preformattedSelectedData.title }}</span>
            <div>
              <b-button
                v-tooltip="$tc('global.configurePreformatted')"
                class="btn__icon btn__icon--primary px-2"
                qa="preformatted-modal-sidepanel-configuration-button"
              >
                <i
                  aria-hidden="true"
                  class="far fa-cog"
                  @click="onConfigurePreformattedClick"
                />
              </b-button>
              <b-button
                class="btn__icon btn__icon--primary ml-2"
                qa="preformatted-modal-sidepanel-close-button"
                @click="selectedTemplate=false"
              >
                <i
                  aria-hidden="true"
                  class="far fa-times"
                />
              </b-button>
            </div>
          </div>
          <div class="text-edition pt-3 px-4 pb-4">
            <b-row class="h-100">
              <b-col>
                <b-tabs
                  pills
                  card
                  vertical
                  class="vt-tabs--vertical"
                >
                  <b-tab active>
                    <template slot="title">
                      <i
                        class="far fa-align-justify"
                        qa="preformatted-modal-sidepanel-macroscopy-tab"
                        aria-hidden="true"
                      />
                    </template>
                    <div class="ml-2 d-flex flex-column h-100">
                      <b-row class="d-flex align-items-center mt-3 mb-2">
                        <b-col>
                          <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.macroscopy') }}</span>
                        </b-col>
                      </b-row>
                      <b-row
                        class="flex-grow-1 overflow-y-auto"
                        qa="preformatted-modal-sidepanel-macroscopy-content"
                      >
                        <b-col>
                          <vt-rich-text-editor
                            class="mt-1 descriptionText"
                            :value="preformattedSelectedData.macro ? preformattedSelectedData.macro.text : ''"
                            :disabled="true"
                            :autofocus="true"
                          />
                        </b-col>
                      </b-row>
                    </div>
                  </b-tab>
                  <b-tab>
                    <template slot="title">
                      <i
                        aria-hidden="true"
                        class="fal fa-microscope"
                        qa="preformatted-modal-sidepanel-microscopy-tab"
                      />
                    </template>
                    <div class="ml-2 d-flex flex-column h-100">
                      <b-row class="d-flex align-items-center mt-3 mb-2">
                        <b-col>
                          <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.microscopy') }}</span>
                        </b-col>
                      </b-row>
                      <b-row
                        class="flex-grow-1 overflow-y-auto"
                        qa="preformatted-modal-sidepanel-microscopy-content"
                      >
                        <b-col>
                          <vt-rich-text-editor
                            class="mt-1 descriptionText"
                            :value="preformattedSelectedData.micro ? preformattedSelectedData.micro.text : ''"
                            :disabled="true"
                            :autofocus="true"
                          />
                        </b-col>
                      </b-row>
                    </div>
                  </b-tab>
                  <b-tab>
                    <template slot="title">
                      <i
                        class="fal fa-comment-alt-medical"
                        aria-hidden="true"
                        qa="preformatted-modal-sidepanel-diagnosis-tab"
                      />
                    </template>
                    <div class="ml-2 d-flex flex-column h-100">
                      <b-row class="d-flex align-items-center mt-3 mb-2">
                        <b-col>
                          <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.diagnosis') }}</span>
                        </b-col>
                      </b-row>
                      <b-row
                        class="flex-grow-1 overflow-y-auto"
                        qa="preformatted-modal-sidepanel-diagnosis-content"
                      >
                        <b-col>
                          <vt-rich-text-editor
                            class="mt-1 descriptionText"
                            :value="preformattedSelectedData.diagnosis ? preformattedSelectedData.diagnosis.text : ''"
                            :disabled="true"
                            :autofocus="true"
                          />
                        </b-col>
                      </b-row>
                    </div>
                  </b-tab>
                  <b-tab>
                    <template slot="title">
                      <i
                        class="fal fa-files-medical"
                        aria-hidden="true"
                        qa="preformatted-modal-sidepanel-additional-report-tab"
                      />
                    </template>
                    <div class="ml-2 d-flex flex-column h-100">
                      <b-row class="d-flex align-items-center mt-3 mb-2">
                        <b-col>
                          <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.additionalReport') }}</span>
                        </b-col>
                      </b-row>
                      <b-row
                        class="flex-grow-1 overflow-y-auto"
                        qa="preformatted-modal-sidepanel-additional-report-content"
                      >
                        <b-col>
                          <vt-rich-text-editor
                            class="mt-1 descriptionText"
                            :value="preformattedSelectedData.additionalReport ? preformattedSelectedData.additionalReport.text : ''"
                            :disabled="true"
                            :autofocus="true"
                          />
                        </b-col>
                      </b-row>
                    </div>
                  </b-tab>
                  <b-tab>
                    <template slot="title">
                      <i
                        class="fal fa-project-diagram"
                        aria-hidden="true"
                        qa="preformatted-modal-sidepanel-codifications-tab"
                      />
                    </template>
                    <div class="ml-2 d-flex flex-column h-100">
                      <b-row class="d-flex align-items-center mt-3 mb-2">
                        <b-col>
                          <span class="ap-title ap-title__main">{{ $t('diagnosis.sections.codifications') }}</span>
                        </b-col>
                      </b-row>
                      <div
                        v-if="preformattedSelectedData.codifications.length"
                        class="flex-grow-1 overflow-y-auto overflow-x-hidden"
                        qa="preformatted-modal-sidepanel-codifications-content"
                      >
                        <b-row class="mt-2 mb-3 header-row">
                          <b-col class="filter-label">
                            {{ $t('diagnosis.labels.topographic') }}
                          </b-col>
                          <b-col class="filter-label">
                            {{ $t('diagnosis.labels.morphological') }}
                          </b-col>
                        </b-row>
                        <b-row
                          v-for="codification in preformattedSelectedData.codifications"
                          :key="codification.id"
                          class="codifications-row"
                          qa="preformatted-modal-sidepanel-codifications-row"
                        >
                          <b-col
                            class="label-dark"
                            qa="preformatted-modal-sidepanel-codifications-topograhic-col"
                          >
                            {{ formatTopographicCode(codification.topographicCode) }}
                          </b-col>
                          <b-col
                            class="label-dark"
                            qa="preformatted-modal-sidepanel-codifications-morphologic-col"
                          >
                            {{ formatMorphologicCode(codification.morphologicCode) }}
                          </b-col>
                          <b-col cols="12">
                            <div class="horizontal-divider my-2" />
                          </b-col>
                        </b-row>
                      </div>
                    </div>
                  </b-tab>
                </b-tabs>
              </b-col>
            </b-row>
          </div>
          <div class="footer top-bordered p-2 d-flex justify-content-between">
            <b-button
              size="sm"
              variant="outline-secondary"
              qa="preformatted-modal-cancel-button"
              @click="selectedTemplate=false"
            >
              {{ $t('global.cancel') }}
            </b-button>
            <div>
              <b-button
                class="btn--primary"
                size="sm"
                qa="preformatted-modal-add-button"
                @click="forBatchActionsModal ? selectforBatchActionsModal() : applyPreformatted(true)"
              >
                <i
                  class="fal fa-check mr-2"
                  aria-hidden="true"
                />
                {{ $t('global.description.modal.addClose') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right" />
      </div>
    </div>
  </b-modal>
</template>

<script>
import { preformattedServices } from '@/api/resources/common/preformatted/preformatted.service'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'

const PREFORMATTED_UPDATE_CHANNEL = 'preformattedUpdateChannel'

export default {
    name: 'PreformattedModal',
    props: {
        sample: Object,
        forBatchActionsModal: { type: Boolean, default: false, required: false },
        casId: { type: Number, required: false },
        samId: { type: Number, required: false },
        sectionsSummary: { type: Object, required: false }
    },
    data() {
        return {
            preformattedSelectedData: null,
            checkOnlyCompatibles: false,
            preformattedList: [],
            selectedTemplateId: null,
            selectedTemplate: false,
            filter: '',
            broadcastUpdatePreformattedChannel: null,
            uiComponentCode: 'uicomponent.diagnosis.preformattedconfig',
            customConfig: {
                showOnlyCompatible: true
            }
        }
    },
    computed: {
        laboratoryId() {
            return this.$store.getters.labId
        },
        visibleOnlyCompatibleCheck() {
            return !this.forBatchActionsModal
        },
        filteredPreformattedList() {
            return this.preformattedList.filter(listItem => listItem.title.toLowerCase().includes(this.filter.toLowerCase()))
        }
    },
    beforeDestroy() {
        this.broadcastUpdatePreformattedChannel.close()
    },
    methods: {
        onConfigurePreformattedClick() {
            const routeData = this.$router.resolve({ name: 'Preformatted', params: { id: this.selectedTemplateId } })
            window.open(routeData.href, '_blank')
        },
        selectforBatchActionsModal() {
            this.$emit('preformattedSelected', this.preformattedSelectedData)
            this.hide()
        },
        formatTopographicCode(topographicCode) {
            if (!topographicCode) {
                return ''
            }
            return `${topographicCode.code} | ${topographicCode.description}`
        },
        formatMorphologicCode(morphologicCode) {
            if (!morphologicCode) {
                return ''
            }
            return `${morphologicCode.code} | ${morphologicCode.description}`
        },
        async applyPreformatted(closeOnApply) {
            let preformattedToAdd = true
            if (this.sectionsSummary.macroHasData ||
                this.sectionsSummary.microHasData ||
                this.sectionsSummary.diagnosisHasData ||
                this.sectionsSummary.additionalReportHasData ||
                this.sectionsSummary.hasCodifications) {
                preformattedToAdd =
                    await this.$_showConfirmationModal(
                        this.$tc('diagnosis.preformatted.addOrReplace'),
                        this.$tc('diagnosis.preformatted.add'),
                        this.$tc('diagnosis.preformatted.replace'),
                        'confirmationModalGlobal-addOrReplace',
                        false)
            }

            let response
            if (preformattedToAdd) {
                response =
                    await this.$_toCallUnique(
                        preformattedServices.applyPreformattedToSampleByAdd(
                            this.laboratoryId,
                            this.casId,
                            this.samId,
                            this.selectedTemplateId,
                            true),
                        async (error) => {
                            if (error.response.data.code === this.$apiExceptions.PREFORMATTED_CANNOT_ADD_SOME_SECTIONS) {
                                const preformattedErrorResponse =
                                    await this.$_showConfirmationModal(this.$tc('diagnosis.preformatted.exceptionQuestion'))
                                if (preformattedErrorResponse) {
                                    await this.$_toCallUnique(
                                        preformattedServices.applyPreformattedToSampleByAdd(
                                            this.laboratoryId,
                                            this.casId,
                                            this.samId,
                                            this.selectedTemplateId,
                                            false))
                                    this.notifyPreformatted(closeOnApply)
                                }
                            } else {
                                this.$_showErrorModalFromErrorResponse(error)
                            }
                        })
            } else {
                const applyTotalReplace =
                    await this.$_showConfirmationModal(
                        this.$tc('diagnosis.preformatted.totalOrPartial'),
                        this.$tc('diagnosis.preformatted.total'),
                        this.$tc('diagnosis.preformatted.partial'),
                        'confirmationModalGlobal-totalOrPartial',
                        false)
                response =
                    await this.$_toCallUnique(
                        preformattedServices.applyPreformattedToSampleByReplace(
                            this.laboratoryId,
                            this.casId,
                            this.samId,
                            this.selectedTemplateId,
                            applyTotalReplace))
            }

            if (response !== null) {
                this.notifyPreformatted(closeOnApply)
            }
        },
        notifyPreformatted(closeOnApply) {
            this.$_showToastSuccess(this.$tc('diagnosis.preformatted.applySuccess'))
            this.$emit('preformattedApplied')

            if (closeOnApply) {
                this.hide()
            }
        },
        async loadAllPreformatted() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(preformattedServices.getAllPreformatted(this.laboratoryId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            this.preformattedList = response.filter((item) => item.active)
            return true
        },
        async loadCompatiblePreformatted() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(preformattedServices.getAllPreformattedCompatible(this.laboratoryId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            this.preformattedList = response.filter((item) => item.active)
            return true
        },
        setBroadcastUpdatePreformattedChannel() {
            this.broadcastUpdatePreformattedChannel = new BroadcastChannel(PREFORMATTED_UPDATE_CHANNEL)
            this.broadcastUpdatePreformattedChannel.onmessage = async () => {
                await this.loadAllPreformatted()
                this.selectedTemplate = false
                this.selectedTemplateId = null
            }
        },
        async show() {
            this.setBroadcastUpdatePreformattedChannel()
            if (this.visibleOnlyCompatibleCheck) {
                const customConfig = await customConfigResource.get(this.uiComponentCode)
                if (customConfig) {
                    this.customConfig = customConfig.config
                }
                await this.loadPreformatted(this.customConfig.showOnlyCompatible)
            } else {
                await this.loadAllPreformatted()
            }
            this.$refs.preformattedModal.show()
        },
        hide() {
            this.broadcastUpdatePreformattedChannel.close()
            this.$refs.preformattedModal.hide()
        },
        selectTemplate(templateId) {
            this.selectedTemplateId = templateId
            this.selectedTemplate = true
            this.loadDetailsOfPreformatted()
        },
        async loadDetailsOfPreformatted() {
            this.updateLoading(true)
            const [err, response] = await this.toCall(preformattedServices.getPreformattedById(this.laboratoryId, this.selectedTemplateId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.preformattedSelectedData = response
        },
        async loadPreformatted(onlyCompatible) {
            if (onlyCompatible) {
                await this.loadCompatiblePreformatted()
            } else {
                await this.loadAllPreformatted()
            }
        },
        async onShowOnlyCompatibleChanged(checkBoxValue) {
            if (this.visibleOnlyCompatibleCheck) {
                await customConfigResource.save(this.uiComponentCode, this.customConfig)
            }
            await this.loadPreformatted(checkBoxValue)
        }
    }
}
</script>

<style lang="scss">
  .preformatted-modal .descriptionText .ql-toolbar {
    display: none;
  }
  .preformatted-modal .descriptionText .ql-container {
    border-top: 1px solid #ccc !important;
    word-break: break-word;
  }
.preformatted-modal .col-fixed {
  width: 20px;
}

.preformatted-modal .modal-body {
  padding: 0.6rem 1.1rem 0 1.1rem !important;
  overflow: hidden;
}

.preformatted-modal .field-edition {
  width: 100px;
  display: inline-block;
  font-size: 1rem;
  border: none;
  border-bottom: 1px solid grey;
  border-radius: 0;
  height: calc(0.6em + 0.5rem + 0px) !important;
  color: vt-color(primary);
}

.field-edition:focus {
  border-color: vt-color(primary);
}

.preformatted-modal .tabs.row {
  border: 1px solid rgba(0, 0, 0, 0.125);
  height: 100%;
}

.preformatted-modal .tab-content {
  padding-bottom: 1.5rem;
}

.preformatted-modal .tab-pane {
  height: 100%;
}
</style>

<style lang="scss" scoped>
.preformatted-table {
    position: relative;
    overflow: hidden;
    margin: 1rem 0;
    border: 1px solid vt-color(general-border);
}

.preformatted-modal .main {
  font-size: font-size(normal);
  margin-bottom: 0;
}

.preformatted-modal .text {
  font-size: font-size(normal);
}

.preformatted-modal .header-row {
  font-size: font-size(small);
}

.preformatted-modal .file {
  border: 1px solid rgba(0, 0, 0, 0.2);
  width: 100%;
  border-radius: 5px;
  transition: 0.2s;
  height: calc(1.5em + 0.5rem + 2px);
}

.preformatted-modal .file:hover {
  border: 1px solid vt-color(primary);
  cursor: pointer;
}

.preformatted-modal .file:hover span {
  color: vt-color(primary);
}

.preformatted-modal .file .icons {
  float: right;
  font-size: 1rem;
}

.file span {
  color: vt-color(base-text);
  font-size: 13px;
}

.template-sidepanel {
  width: 100%;
  background: #fff;
  z-index: 3;
  border-left: 1px solid #dee2e6;
  height: 100%;
}

.template-sidepanel .header {
  border-bottom: 1px solid #dee2e6;
}

.template-sidepanel .footer {
  border-top: 1px solid #dee2e6;
  position: absolute;
  bottom: 0;
  width: 100%;
}

.text-edition {
  color: #303133;
  text-align: justify;
  height: 100%;
  flex-grow: 1;
  min-height: 0;
  margin-bottom: 40px;
}

.text-edition .title {
  color: vt-color(primary);
  font-size: font-size(big);
}

.panel-visible {
  position: absolute;
  top: 0;
  right: -620px;
  height: 100%;
  width: 610px;
  z-index: 1;
  transition: 0.3s;
}

.active {
  right: 0;
}

.preformatted-modal {
  color: #303133;
}

.preformatted-modal .filters-header {
  border-bottom: 1px solid #dee2e6;
}

.preformatted-modal .filters-header input {
  width: 100%;
}

.preformatted-modal .filters-header span {
  font-size: 0.9rem;
}

.preformatted-modal .table-header {
  background: #f4f4f4;
  border: 1px solid #dee2e6;
  color: rgb(48, 49, 51);
}

.preformatted-modal .table-content {
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

.preformatted-modal .table-row {
  border-bottom: 1px solid #dee2e6;
  font-size: 0.9rem;
  transition: 0.3s;
  color: rgb(48, 49, 51);
}

.preformatted-modal .table-row:hover {
  background: #f9f9f9;
  cursor: pointer;
}

.preformatted-modal .table-row .table-row-tittle {
  transition: 0.3s;
}

.preformatted-modal .table-row:hover .table-row-tittle {
  color: vt-color(primary);
}

.preformatted-modal .table-row .table-row-desc {
  text-align: justify;
}

.preformatted-modal .row-active {
  border-left: 3px solid vt-color(primary);
  background: #f9f9f9;
}

.preformatted-modal .row-active .table-row-tittle {
  color: vt-color(primary);
}

.preformatted-modal .table-content {
  height: calc(85vh - 120px);
  min-height: 560px;
  overflow: auto;
}

.preformatted-modal .btn-filter {
  display: none;
}

.preformatted-modal .filter-input:focus {
  border-color: vt-color(primary);
}

.preformatted-modal .btn-filter {
  position: absolute;
  right: 5px;
  top: 0;
}
</style>
